import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import DataTable from "react-data-table-component";
import {
  Box,
  Typography,
  Alert,
  Paper,
  TextField,
  IconButton,
  CircularProgress,
  Stack,
  Tooltip,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";

export default function ListPronoun({ chatbotId, canDelete }) {
  const theme = useTheme();
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    document.title = "Pronoun Lists | KlonIT Web Application";
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    setError("");
    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      const response = await axiosInstance.get(`/get-pronoun`, { params });
      const responseData =
        typeof response.data.data === "string"
          ? JSON.parse(response.data.data)
          : response.data.data;

      setDocuments(responseData);
      setFilteredDocuments(responseData);
    } catch (error) {
      console.error("Error fetching Pronoun:", error);
      setError("Failed to fetch pronouns.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      await axiosInstance.delete(`/delete-pronoun/${id}`, { params });
      setDocuments((prev) => prev.filter((doc) => doc.id !== id));
      setFilteredDocuments((prev) => prev.filter((doc) => doc.id !== id));
      setError("Pronoun deleted successfully.");
    } catch (error) {
      console.error("Error deleting pronoun:", error);
      setError("Failed to delete pronoun.");
    }
  };

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchText(value);
    setFilteredDocuments(
      documents.filter((doc) => doc.pronoun.toLowerCase().includes(value))
    );
  };

  const customDataTableStyles = {
    table: {
      style: {
        backgroundColor: theme.palette.background.paper,
      },
    },
    headRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#cbd5e1"
        }`,
      },
    },
    headCells: {
      style: {
        fontWeight: 600,
        fontSize: "14px",
        color: theme.palette.text.primary,
      },
    },
    rows: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc",
        },
      },
    },
    pagination: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderTop: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
        }`,
      },
    },
    expanderRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#0f172a" : "#f9fafb",
      },
    },
  };


  const columns = [
    { name: "Pronoun", selector: (row) => row.pronoun, sortable: true,
       cell: (row) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} color="textPrimary">
            {row.pronoun}
          </Typography>
        </Box>
      ),
     },
    { name: "Replace Pronoun", selector: (row) => row.replace_noun, sortable: true,
       cell: (row) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} color="textPrimary">
            {row.replace_noun}
          </Typography>
        </Box>
      ) },
    { name: "Created Date", selector: (row) => row.created_date, sortable: true,
       cell: (row) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} color="textPrimary">
            {row.created_date}
          </Typography>
        </Box>
      ) },
    ...(canDelete
      ? [
          {
            name: "Action",
            cell: (row) => (
              <Tooltip title="Delete Pronoun">
                <IconButton color="error" onClick={() => handleDelete(row.id)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            ),
          },
        ]
      : []),
  ];

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={700} color="textPrimary">
          Pronoun List
        </Typography>
        <TextField
          size="small"
          placeholder="Search pronoun..."
          value={searchText}
          onChange={handleSearch}
          InputProps={{
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
          }}
          sx={{ width: 250 }}
        />
      </Stack>

      {error && (
        <Alert
          severity={
            error.includes("deleted") || error.includes("successfully")
              ? "success"
              : "error"
          }
          sx={{ mb: 2 }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <Stack alignItems="center" spacing={2}>
            <CircularProgress />
            <Typography>Loading Pronouns...</Typography>
          </Stack>
        </Box>
      ) : filteredDocuments.length === 0 ? (
        <Typography>No pronouns found.</Typography>
      ) : (
        <DataTable
          columns={columns}
          data={filteredDocuments}
          pagination
          highlightOnHover
          dense
          customStyles={customDataTableStyles}
        />
      )}
    </Paper>
  );
}
